// RUTA: src/lib/celebrate.ts
//
// Helpers para celebrar momentos motivacionales (meta cumplida, hito
// de racha) con confetti + gating por día para que cada celebración
// solo dispare 1 vez al día.
//
// Patrones de uso:
//   celebrateOnce('cardio-goal-2026-05-08', () => fireGoalConfetti())
//
// Si el usuario tiene `prefers-reduced-motion: reduce`, NO se
// dispara el confetti (es decorativo, no funcional).

import confetti from 'canvas-confetti';

// Bug previo (Joel): el storage era sessionStorage. Cada vez que
// el cliente cerraba y abría la PWA, la "session" era nueva y el
// confeti volvía a disparar aunque ya hubiera celebrado el logro
// del día. Resultado: confeti molesto cada cold-start.
//
// Fix: usar localStorage. Como las keys ya incluyen la fecha
// (todayKey() = YYYY-MM-DD), el gating es POR DÍA real — celebra
// solo la primera vez de cada día sin importar cuántas veces
// abra/cierre la app.
//
// Cleanup: al cargar, limpiamos entries antiguas (más de 7 días)
// para no llenar localStorage con keys de hace meses.
const STORAGE_KEY = 'iftmx_celebrations';

interface StoredCelebration {
  /** Fecha YYYY-MM-DD cuando se celebró por primera vez. */
  d: string;
}

function readStore(): Record<string, StoredCelebration> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}
function writeStore(store: Record<string, StoredCelebration>) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    /* ignore quota / private mode */
  }
}

/**
 * Limpia entries con fecha mayor a 7 días para no acumular basura
 * en localStorage. Se llama al inicio de cada celebración.
 */
function pruneOld(store: Record<string, StoredCelebration>): Record<string, StoredCelebration> {
  const today = new Date();
  const cutoff = new Date(today);
  cutoff.setDate(today.getDate() - 7);
  const cutoffStr = `${cutoff.getFullYear()}-${String(cutoff.getMonth() + 1).padStart(2, '0')}-${String(cutoff.getDate()).padStart(2, '0')}`;

  const pruned: Record<string, StoredCelebration> = {};
  for (const [key, val] of Object.entries(store)) {
    if (val.d >= cutoffStr) pruned[key] = val;
  }
  return pruned;
}

/**
 * Dispara `fn` solo la primera vez que se llama con `key` PARA EL
 * DÍA ACTUAL. Persistencia en localStorage para que cerrar/abrir
 * la app NO reinicie las celebraciones del día.
 *
 * Las keys deben incluir la fecha (todayKey()) para que cada día
 * sea una celebración distinta:
 *
 *   celebrateOnce(`cardio-goal-${todayKey()}-${userId}`, () => fireGoalConfetti())
 */
export function celebrateOnce(key: string, fn: () => void) {
  const store = pruneOld(readStore());
  if (store[key]) {
    writeStore(store); // commit pruning
    return;
  }
  store[key] = { d: todayKey() };
  writeStore(store);
  fn();
}

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
}

/**
 * Burst grande de confetti — para metas grandes (cardio 100%,
 * banda verde de calorías por primera vez en el día).
 */
export function fireGoalConfetti() {
  if (prefersReducedMotion()) return;
  const colors = ['#dc2626', '#f59e0b', '#22c55e', '#fbbf24', '#fb923c'];
  // Doble disparo desde dos lados para que se vea pleno
  confetti({
    particleCount: 60,
    angle: 60,
    spread: 60,
    origin: { x: 0.1, y: 0.7 },
    colors,
    disableForReducedMotion: true,
  });
  confetti({
    particleCount: 60,
    angle: 120,
    spread: 60,
    origin: { x: 0.9, y: 0.7 },
    colors,
    disableForReducedMotion: true,
  });
}

/**
 * Burst pequeño — para hitos menores (subir de tier de racha,
 * registrar el primer log del día).
 */
export function fireMilestoneConfetti() {
  if (prefersReducedMotion()) return;
  confetti({
    particleCount: 35,
    spread: 50,
    origin: { y: 0.6 },
    colors: ['#f59e0b', '#fbbf24', '#fb923c', '#dc2626'],
    disableForReducedMotion: true,
  });
}

/**
 * Devuelve la fecha local en formato YYYY-MM-DD — usable para keys
 * de celebración (gated by day).
 */
export function todayKey(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Tiers de racha que disparan celebración (1ra vez al cruzar).
 * Usado para detectar hito y dar pulse + confetti.
 */
export const STREAK_MILESTONES = [3, 7, 14, 30, 60, 100] as const;

/** ¿Acabas de cruzar uno de los milestones? */
export function isStreakMilestone(streak: number): boolean {
  return (STREAK_MILESTONES as readonly number[]).includes(streak);
}
